import React, { useState, useEffect } from 'react';
import { API_BASE_URL, getAuthHeaders } from '../utils/utils';
import { event } from '../utils/gtm';

const FeedbackModal = ({ isOpen, onClose, onModalClose }) => {
    const [feedbackText, setFeedbackText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');

    // Reset state when modal is closed/opened for a clean slate
    useEffect(() => {
        if (isOpen) {
            setFeedbackText('');
            setMessage('');
            setIsLoading(false);
        }
    }, [isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!feedbackText.trim()) {
            setMessage('Please enter your feedback before submitting.');
            return;
        }
        setIsLoading(true);
        setMessage('');

        try {
            event({ action: 'submit_form', category: 'Engagement', label: 'Feedback' });
            const response = await fetch(`${API_BASE_URL}/feedback`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ feedback_text: feedbackText }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || data.error || 'An unknown error occurred.');
            }

            setMessage('Thank you! Your feedback has been submitted.');
            // Automatically close the modal after showing the success message
            setTimeout(() => {
                onClose();
                if (onModalClose) {
                    onModalClose();
                }
            }, 2500);

        } catch (error) {
            setMessage(error.message);
            setIsLoading(false); // Allow user to try again on error
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 p-4" onClick={onClose}>
            <div className="bg-gray-800 text-white p-6 rounded-lg shadow-xl w-full max-w-md relative" onClick={(e) => e.stopPropagation()}>
                <h2 className="text-xl font-bold mb-2">Share Your Feedback</h2>
                <p className="text-sm text-gray-400 mb-4">We'd love to hear your thoughts on how we can improve.</p>
                <button onClick={() => {
                    onClose();
                    if (onModalClose) {
                        onModalClose();
                    }
                }} className="absolute top-4 right-4 text-gray-400 hover:text-white" aria-label="Close modal">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <form onSubmit={handleSubmit}>
                    <textarea
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        placeholder="Your suggestions, bug reports, or feature requests..."
                        className="w-full h-32 p-2 rounded bg-gray-700 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                        disabled={isLoading || message.includes('Thank you')}
                    />
                    <button
                        type="submit"
                        disabled={isLoading || !feedbackText.trim() || message.includes('Thank you')}
                        className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded transition-colors duration-200 disabled:bg-indigo-800 disabled:cursor-not-allowed"
                    >
                        {isLoading ? 'Submitting...' : 'Submit'}
                    </button>
                    {message && <p className={`mt-3 text-center text-sm ${message.includes('Thank you') ? 'text-green-400' : 'text-red-400'}`}>{message}</p>}
                </form>
            </div>
        </div>
    );
};

export default FeedbackModal;